/**
 * Runner identity presentation: portraits from the token's own metadata (RunnerIdentity.tokenURI — on-chain JSON + SVG
 * from the base renderer), the base-renderer check recomputed in the browser, owner overlays, and the local
 * "Equip my Runner" preference. Equipping here changes only THIS viewer's celebration, result card and profile — the
 * shared runner on the road is drawn the same for everyone and is never affected.
 */
import { shoeOutcome } from '../data/racePresentation';
import { Link } from '../app/router';
import { useEffect, useState, useSyncExternalStore } from 'react';
import { Mascot } from '@runner/ui';
import { TRAIT_TITLES } from '@runner/simulation';
import type { Hex } from 'viem';
import { useWorld, useWorldCtx } from '../data/WorldProvider';
import { decodeSvgImage, decodeTokenUri, identitySvg, svgDataUri } from '../data/model';
import type { IdentityDetail, WorldView } from '../data/types';
import { units } from '../lib/format';

// ------------------------------------------------------------------------------------------------
// Local equip preference (per data source; this browser only)
// ------------------------------------------------------------------------------------------------

export interface EquipPref {
  tokenId: string | null;
  overlays: boolean;
}

const listeners = new Set<() => void>();
const cache = new Map<string, EquipPref>();
const KEY = (mode: string) => `runner.equip.${mode}`;

function readPref(mode: string): EquipPref {
  const hit = cache.get(mode);
  if (hit) return hit;
  let v: EquipPref = { tokenId: null, overlays: true };
  try {
    const raw = localStorage.getItem(KEY(mode));
    if (raw) {
      const j = JSON.parse(raw) as Partial<EquipPref>;
      v = { tokenId: typeof j.tokenId === 'string' ? j.tokenId : null, overlays: j.overlays !== false };
    }
  } catch {
    /* storage unavailable: the preference lasts for this page only */
  }
  cache.set(mode, v);
  return v;
}

export function useEquipPref(): [EquipPref, (p: EquipPref) => void] {
  const { mode } = useWorldCtx();
  const pref = useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    () => readPref(mode),
  );
  const set = (p: EquipPref) => {
    cache.set(mode, p);
    try {
      localStorage.setItem(KEY(mode), JSON.stringify(p));
    } catch {
      /* ignore */
    }
    for (const l of listeners) l();
  };
  return [pref, set];
}

// ------------------------------------------------------------------------------------------------
// Identity detail (fetched from the source; refreshed when the view version moves on)
// ------------------------------------------------------------------------------------------------

export function useIdentity(tokenId: string | null, refresh = 0): { detail: IdentityDetail | null; error: string | null } {
  const { source } = useWorld();
  const [detail, setDetail] = useState<IdentityDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!tokenId) {
      setDetail(null);
      return;
    }
    let stop = false;
    setDetail(null);
    setError(null);
    source
      .identity(BigInt(tokenId))
      .then((d) => {
        if (stop) return;
        setDetail(d);
        setError(null);
      })
      .catch((e: Error) => !stop && setError(e.message));
    return () => {
      stop = true;
    };
  }, [source, tokenId, refresh]);
  return { detail, error };
}

// ------------------------------------------------------------------------------------------------
// Portrait
// ------------------------------------------------------------------------------------------------

export interface PortraitCheck {
  image: string;
  source: 'token metadata' | 'base renderer (recomputed)';
  /** The metadata image equals the base renderer recomputed in this browser (null when not comparable). */
  matchesBase: boolean | null;
}

export function portraitOf(d: IdentityDetail): PortraitCheck {
  const meta = decodeTokenUri(d.metadataUri);
  const base = identitySvg(d.traits.seed as Hex, BigInt(d.tokenId));
  if (meta) {
    const svg = decodeSvgImage(meta.image);
    return { image: meta.image, source: 'token metadata', matchesBase: svg === null ? null : svg === base };
  }
  return { image: svgDataUri(base), source: 'base renderer (recomputed)', matchesBase: null };
}

const SLOT_COLOR: Record<string, string> = { shoes: 'var(--orange)', jacket: 'var(--harvest)', bib: 'var(--gold)', animation: 'var(--water)' };

/** Equipped overlays as labelled chips around the portrait (cosmetic only; the approved art itself is unchanged). */
export function OverlayChips({ d }: { d: IdentityDetail }) {
  const on = d.equipment.slots.filter((s) => s.item);
  if (!on.length) return <p className="small muted" style={{ margin: 0 }}>Base look (no overlays equipped).</p>;
  return (
    <div className="pins" data-testid="overlays">
      {on.map((s) => (
        <span key={s.slot} className="tag" style={{ borderColor: SLOT_COLOR[s.slotName] ?? 'var(--ink)' }} data-slot={s.slotName}>
          {s.slotName}: {s.item!.name}
        </span>
      ))}
    </div>
  );
}

export function RunnerPortrait({ d, size = 180, overlays = true, celebrate = false }: { d: IdentityDetail; size?: number; overlays?: boolean; celebrate?: boolean }) {
  const p = portraitOf(d);
  const eq = Object.fromEntries(d.equipment.slots.filter((s) => s.item).map((s) => [s.slotName, s.item!.name]));
  const frame = overlays && eq.jacket ? 'var(--harvest)' : 'var(--ink)';
  return (
    <figure className={`portrait${celebrate && overlays && eq.animation ? ' celebrate' : ''}`} style={{ width: size }} data-testid="portrait" data-token={d.tokenId}>
      <img src={p.image} alt={`Runner #${d.tokenId}: ${d.traits.labels.eyes} eyes, ${d.traits.labels.headband.toLowerCase()} headband, ${d.traits.labels.frame.toLowerCase()} frame`} width={size} height={size} style={{ borderColor: frame }} />
      {overlays && eq.shoes && <span className="overlay-band shoes">{eq.shoes}</span>}
      {overlays && eq.bib && <span className="overlay-badge bib">{eq.bib}</span>}
    </figure>
  );
}

export function TraitList({ d }: { d: IdentityDetail }) {
  const l = d.traits.labels;
  const rows: [string, string][] = TRAIT_TITLES.map(([k, title]) => [title, l[k]]);
  return (
    <dl className="kv" data-testid="traits">
      {rows.map(([k, v]) => (
        <div key={k} style={{ display: 'contents' }}>
          <dt>{k}</dt>
          <dd data-trait={k}>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

// ------------------------------------------------------------------------------------------------
// Equip my Runner (local) and the viewer's own result card
// ------------------------------------------------------------------------------------------------

export function EquipControl({ view }: { view: WorldView }) {
  const [pref, setPref] = useEquipPref();
  const owned = view.runners?.owned ?? [];
  const selected = pref.tokenId && owned.some((o) => o.tokenId === pref.tokenId) ? pref.tokenId : '';
  return (
    <div className="stack" style={{ gap: 6 }} data-testid="equip-control">
      <label className="stack" style={{ gap: 4 }}>
        <span className="small">Equip my Runner (only your own view)</span>
        <select
          className="field"
          value={selected}
          onChange={(e) => setPref({ ...pref, tokenId: e.target.value || null })}
          aria-label="Equip my Runner"
          disabled={!owned.length}
        >
          <option value="">{owned.length ? 'Standard RUNNER' : 'No Runner owned — standard RUNNER'}</option>
          {owned.map((o) => (
            <option key={o.tokenId} value={o.tokenId}>
              Runner #{o.tokenId} · {o.traits.eyes} eyes, {o.traits.frame.toLowerCase()} frame
            </option>
          ))}
        </select>
      </label>
      <label className="small" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input type="checkbox" checked={pref.overlays} onChange={(e) => setPref({ ...pref, overlays: e.target.checked })} />
        Show its equipped overlays
      </label>
      <p className="small muted" style={{ margin: 0 }}>
        Cosmetic, in this browser only. The shared runner on the road looks the same for everyone.
      </p>
    </div>
  );
}

/** The viewer's own result card for a finished run, with their equipped Runner celebrating. */
export function ResultCard({ view }: { view: WorldView }) {
  const [pref] = useEquipPref();
  const run = view.run;
  const ownedOk = !!pref.tokenId && !!view.runners?.owned.some((o) => o.tokenId === pref.tokenId);
  const { detail } = useIdentity(ownedOk ? pref.tokenId : null, view.runners?.owned.length ?? 0);
  if (!run || run.status !== 'finished') return null;
  const e = view.road?.runId === run.id ? view.road.escrow : null;
  const q = view.quote;
  const outcome = shoeOutcome(view);
  return (
    <section className="panel result-card" aria-labelledby="result-h" data-testid="result-card" data-equipped={ownedOk && detail ? detail.tokenId : 'standard'}>
      <span className="account-eyebrow">RUN {run.id} RESULT</span><h2 id="result-h">{outcome.title}</h2><p>{outcome.detail}</p>
      <p className="small">{view.user.connected ? run.shoe?.yourWeight ? "Your wallet had eligible Shoe entries. Entry does not guarantee a win." : "No eligible Shoe entries are reported for your wallet in this race." : "Connect your wallet to see your entry status and claimable rewards."}</p>
      <Link to="/account" className="btn">Your account & claims ↗</Link>
      <details className="result-ledger"><summary>Race outcome and accounting</summary>
      <div className="result-row">
        {ownedOk && detail ? <RunnerPortrait d={detail} size={120} overlays={pref.overlays} celebrate /> : <Mascot pose="celebrate" size={110} title="RUNNER celebrating" />}
        <div className="small" style={{ flex: 1, minWidth: 160 }}>
          <p style={{ margin: '0 0 6px', fontWeight: 700 }}>
            {run.abandoned
              ? e && BigInt(e.assigned) > 0n
                ? 'Abandoned mid-course: assigned value stayed; the rest returned.'
                : 'Abandoned: its unassigned budget returned to the road reserve.'
              : run.reachedFinish === false ? 'Stopped at the step bound.' : `Finished on the ${run.exitRoute ?? 'neutral'} route.`}
          </p>
          {e && (
            <ul style={{ margin: 0, paddingLeft: 16 }}>
              <li>
                {units(BigInt(e.toHolders), q.decimals)} {q.symbol} → depositors (round {e.cohortEpoch})
              </li>
              <li>
                {units(BigInt(e.toForge), q.decimals)} {q.symbol} → Forge reserve
              </li>
              <li>
                {units(BigInt(e.returnedToRoad ?? '0'), q.decimals)} {q.symbol} returned to the road reserve
              </li>
            </ul>
          )}
          {run.shoe && <p style={{ margin: '6px 0 0' }}>Golden Shoe: {run.shoe.status.toLowerCase()}{run.shoe.winnerLabel ? ` · winner ${run.shoe.winnerLabel}` : ''}</p>}
          <p className="muted" style={{ margin: '6px 0 0' }}>
            {ownedOk ? `Runner #${pref.tokenId} celebrates — your view only.` : 'NFTs are optional. Deposit rewards and trading prizes are separate.'}
          </p>
        </div>
      </div>
      </details>
    </section>
  );
}
